import { Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

// validators
import {
  validateName,
  validatePhone,
  validateZip,
  validateAddress1,
  validateHeightInches,
  validateHeightFeet,
  multipleCheckboxRequired,
} from 'src/app/validators';

// models
import { QuestionBase } from 'src/app/models/question-base';
import { optionsMap } from 'src/app/models/options-map';

// utility
import { find, clone, cloneDeep, pickBy, isArray, mapValues, filter, flatten, every, isEmpty, values, forEach, isObject, orderBy } from 'lodash';

// services
import { QuestionService } from './question.service';

@Injectable({
  providedIn: 'root'
})

export class QuestionControlService {
  optionsMap: any = optionsMap;
  preFillArrayMap: any = null;
  baseQuestions: any;

  constructor(
    private builder: FormBuilder,
    private qs: QuestionService
  ) {}

  // questionType: Address
  initAddressGroup() {
    return this.builder.group({
      Street: [null, Validators.compose([Validators.required, Validators.maxLength(100), validateAddress1])],
      Street2: [null, Validators.maxLength(100)],
      City: [null, Validators.compose([Validators.required, Validators.maxLength(30),validateAddress1])],
      State: [null, Validators.required],
      Zip: [null, Validators.compose([Validators.required, Validators.minLength(5), Validators.maxLength(5), validateZip])]
    });
  }

  // questionType: File
  initFileUploadGroup(optional?: boolean) {
    if (optional) {
      return this.builder.group({
        Upload: [null, null],
        Response: [null, null]
      });
    } else {
      return this.builder.group({
        Upload: [null, Validators.required],
        Response: [null, Validators.required]
      });
    }
  }

  // questionType: Name
  initNameGroup() {
    return this.builder.group({
      Title: [null],
      FirstName: [null, Validators.compose([Validators.required, Validators.maxLength(50), validateName])],
      MiddleName: [null, Validators.compose([Validators.maxLength(30), validateName])],
      LastName: [null, Validators.compose([Validators.required, Validators.maxLength(50), validateName])],
      Suffix: [null]
    });
  }

  // questionType: Physician
  initPhysicianGroup() {
    return this.builder.group({
      Search: [null],
      StateFilter: [null],
      Name: [null, Validators.compose([Validators.required, Validators.maxLength(70), validateName])],
      Phone: [null, Validators.compose([Validators.required, validatePhone])],
      Street: [null, Validators.compose([Validators.required, Validators.maxLength(100),validateAddress1])],
      Street2: [null, Validators.maxLength(100)],
      City: [null, Validators.compose([Validators.required, Validators.maxLength(30),validateAddress1])],
      State: [null, Validators.required],
      Zip: [null, Validators.compose([Validators.required, Validators.minLength(5), Validators.maxLength(5),validateZip])]
    });
  }

  // questionType: Length
  initLengthGroup() {
    return this.builder.group({
      Feet: [null, Validators.compose([Validators.required, Validators.maxLength(2), validateHeightFeet])],
      Inches: [null, Validators.compose([Validators.required, Validators.maxLength(2), validateHeightInches])],
    });
  }

  // questionType: BloodPressure
  initBloodPressureGroup(optional?: boolean) {
    if (optional) {
      return this.builder.group({
        Systolic: [null, Validators.maxLength(3)],
        Diastolic: [null, Validators.maxLength(3)]
      });
    } else {
      return this.builder.group({
        Systolic: [null, Validators.compose([Validators.required, Validators.maxLength(3)])],
        Diastolic: [null, Validators.compose([Validators.required, Validators.maxLength(3)])]
      });
    }
  }

  // FormGroups for IUL mat-table components
  toIULTableFormGroup(questions: QuestionBase<any>[], percent = false, optional?) {
    const group: any = {};

    questions.forEach(question => {

      const { QuestionName } = question;
      group[QuestionName] = this.generateQuestionForm(question);

    });

    if (percent) {
      const PercentTotal = {'QuestionName': 'PercentTotal', 'QuestionType': 'Integer', 'Units': '%', 'IsOptional': true};
      group['PercentTotal'] = optional ? [null, this.qs.generateValidators(PercentTotal, false)] : [null, this.qs.generateValidators(PercentTotal, true)];
    } else {
      const DollarTotal = {'QuestionName': 'DollarTotal', 'QuestionType': 'Integer', 'Units': '$', 'IsOptional': true};
      group['DollarTotal'] = [null, this.qs.generateValidators(DollarTotal, true)];
    }
    return this.builder.group(group);
  }


  // generate formGroups | formArrays | custom forms
  generateQuestionForm(question: any) {

    const { QuestionType, QuestionName, controlType, QuestionOptions, ChildQuestions, MultipleResponse } = question;

    const validatorsCompose = this.qs.generateValidators(question);

    // checkbox control type
    if (controlType === 'checkbox') {

      // create formArray & add validation for checkboxes
      const controls = QuestionOptions.map(control => [question.value || false]);
      if (question.IsOptional) {
        return this.builder.array(controls);
      } else {
        return this.builder.array(controls, multipleCheckboxRequired());
      }

      // COMPOUND QUESTIONS
    } else if (QuestionType === 'Compound' && MultipleResponse) {
      const children = ChildQuestions.map((value) => {
        return value.QuestionName;
      });

      let grandchildren = filter(this.baseQuestions, (item) => {
        return children.includes(item.QuestionName) && isArray(item.ChildQuestions) && item.ChildQuestions.length > 0;
      });

      grandchildren = grandchildren.map((item) => {
        return item.ChildQuestions;
      });
      grandchildren = flatten(grandchildren);
      let greatgrandchildren;
      let greatgreatgrandchildren;

      if (isArray(grandchildren) && grandchildren.length > 0) {

        const grandkids = grandchildren.map((value) => {
          return Object.values(value).toString();
        });

        greatgrandchildren = filter(this.baseQuestions, (item) => {
          return grandkids.includes(item.QuestionName) && isArray(item.ChildQuestions) && item.ChildQuestions.length > 0;
        });
        greatgrandchildren = greatgrandchildren.map((item) => {
          return item.ChildQuestions;
        });
        greatgrandchildren = flatten(greatgrandchildren);

        if (isArray(greatgrandchildren) && greatgrandchildren.length > 0) {

          const greatgrandkids = greatgrandchildren.map((value) => {
            return Object.values(value).toString();
          });

          greatgreatgrandchildren = filter(this.baseQuestions, (item) => {
            return greatgrandkids.includes(item.QuestionName) && isArray(item.ChildQuestions) && item.ChildQuestions.length > 0;
          });
          greatgreatgrandchildren = greatgreatgrandchildren.map((item) => {
            return item.ChildQuestions;
          });
          greatgreatgrandchildren = flatten(greatgreatgrandchildren);

        }

      }

      // determine number of array items
      const arraySeed = [];

      // check if it has a parent
      const arrayParent = find(this.baseQuestions, (item) => {
        return item.QuestionName == question.ParentQuestionName;
      });

      const multipleResponseAncestors = this.ancestorIsMultpleResponse(this.baseQuestions, arrayParent ? arrayParent : {MultipleResponse: false}, []);

      const isNestedArray = (arrayParent && multipleResponseAncestors.includes(true)) ? true : false;

      // determine number of array items to start
      let arrayItems: any = this.preFillArrayMap[QuestionName] || 1;
      arrayItems = isNestedArray && this.preFillArrayMap['nested'] ? this.preFillArrayMap['nested'][QuestionName] || 1 : arrayItems;

      // create array of form groups
      const childrenAugmented = isArray(greatgreatgrandchildren) && greatgreatgrandchildren.length > 0 ? [...ChildQuestions, ...grandchildren, ...greatgrandchildren, ...greatgreatgrandchildren] : isArray(greatgrandchildren) && greatgrandchildren.length > 0 ? [...ChildQuestions, ...grandchildren, ...greatgrandchildren] : isArray(grandchildren) && grandchildren.length > 0 ? [...ChildQuestions, ...grandchildren] : ChildQuestions;

      for (let step = 0; step < arrayItems; step++) {
        arraySeed.push(this.toFormGroup(childrenAugmented, true));
      }
      return this.builder.array(arraySeed);

    } else if (QuestionType === 'Compound' && (QuestionName === 'IULPremPaymentAllocation' || QuestionName === 'IULProceedsAllocToCompound' || QuestionName === 'IULFixedAcctAlloc')) {

      const children = ChildQuestions.map((value) => {
        return value.QuestionName;
      });

      const childQuestions = filter(this.baseQuestions, (item) => {
        return children.includes(item.QuestionName);
      });

      return QuestionName === 'IULPremPaymentAllocation' ? this.toIULTableFormGroup(childQuestions, true) : this.toIULTableFormGroup(childQuestions, true, true);

    // commenting for ttdatapr-3823
    // } else if (QuestionType === 'Compound' && QuestionName === 'IULFixedAcctAlloc') {

    //   let children = ChildQuestions.map((value) => {
    //     return value.QuestionName;
    //   });

    //   let childQuestions = filter(this.baseQuestions, (item) => {
    //     return children.includes(item.QuestionName);
    //   });

    //   return this.toIULTableFormGroup(childQuestions, false);

      // Name
    } else if (QuestionType === 'Name') {
      return this.initNameGroup();

      // Address
    } else if (QuestionType === 'Address') {
      return this.initAddressGroup();

      // File Upload
    } else if (QuestionType === 'File') {
      return this.initFileUploadGroup(question.IsOptional);

      // Physician
    } else if (QuestionType === 'Physician') {
      return this.initPhysicianGroup();

      // Length
    } else if (QuestionType === 'Length') {
      return this.initLengthGroup();

      // Blood Pressure
    } else if (QuestionType === 'BloodPressure') {
      return this.initBloodPressureGroup(question.IsOptional);

    } else {
      // default: everything else
      return [question.value || null, validatorsCompose];
    }
  }

  ancestorIsMultpleResponse(questions, object, array) {
    array.push(object.MultipleResponse ? true : false);
    const { ParentQuestionName } = object;
    if (ParentQuestionName) {
      const question = find(questions, { QuestionName: ParentQuestionName});
      return this.ancestorIsMultpleResponse(questions, question, array);
    }
    return array;
  }

  // generate questions and formGroup
  toFormGroup(questions: QuestionBase<any>[], isChildren = false, preFillArrayMap: any = null) {
    const group: any = {};

    // clone questions if not children
    if (!isChildren) {
      this.baseQuestions = clone(questions);
      this.preFillArrayMap = preFillArrayMap;
    } else {
      questions = questions.map(item => this.baseQuestions.find(element => element.QuestionName === item.QuestionName));
      const p = questions.find(item => item.MultipleResponse);
      if (p && p.ChildQuestions) {
        questions = questions.filter(item => !p.ChildQuestions.find(element => element.QuestionName === item.QuestionName));
      }
    }

    questions.forEach(question => {

      const { QuestionName, ParentQuestionName } = question;
      
      const parentQuestion = this.getQuestion(questions, ParentQuestionName);
      const parentIsMultpleResponse = parentQuestion ? parentQuestion.MultipleResponse : false;
      let grandparentIsMultpleResponse: boolean = false;
      let greatGrandparentIsMultpleResponse: boolean = false;
      let greatGreatGrandparentIsMultpleResponse: boolean = false;
      if (parentQuestion) {
        const grandparentQuestionName = parentQuestion.ParentQuestionName ? parentQuestion.ParentQuestionName : null;
        const grandparentQuestion = grandparentQuestionName ? this.getQuestion(questions, grandparentQuestionName) : null;
        grandparentIsMultpleResponse = grandparentQuestion ? grandparentQuestion.MultipleResponse : false;
        if (grandparentQuestion) {
          const greatGrandparentQuestionName = grandparentQuestion.ParentQuestionName ? grandparentQuestion.ParentQuestionName : null;
          const greatGrandparentQuestion = greatGrandparentQuestionName ? this.getQuestion(questions, greatGrandparentQuestionName) : null;
          greatGrandparentIsMultpleResponse = greatGrandparentQuestion ? greatGrandparentQuestion.MultipleResponse : false;
          if (greatGrandparentQuestion) {
            const greatGreatGrandparentQuestionName = greatGrandparentQuestion.ParentQuestionName ? greatGrandparentQuestion.ParentQuestionName : null;
            const greatGreatGrandparentQuestion = greatGreatGrandparentQuestionName ? this.getQuestion(questions, greatGreatGrandparentQuestionName) : null;
            greatGreatGrandparentIsMultpleResponse = greatGreatGrandparentQuestion ? greatGreatGrandparentQuestion.MultipleResponse : false;
          }
        }
      }

      if (question && !parentIsMultpleResponse && !grandparentIsMultpleResponse && !greatGrandparentIsMultpleResponse && !greatGreatGrandparentIsMultpleResponse && ParentQuestionName !== 'IULPremPaymentAllocation' && ParentQuestionName !== 'IULFixedAcctAlloc' && ParentQuestionName !== 'IULProceedsAllocToCompound') {
        group[QuestionName] = this.generateQuestionForm(question);
      }

    });

    return this.builder.group(group);
  }

  toMedicalConditionsFormGroup(questions: QuestionBase<any>[], addArrays?: boolean, prefillContent?: any) {
    const group: any = {};

    // configure array map for building MultipleResponse arrays when pre-filling form
    if (addArrays) {
      this.preFillArrayMap = this.generateArrayMap(prefillContent);
    }
    questions.forEach(question => {
      const { QuestionName, ParentQuestionName } = question;
      group[QuestionName] = this.generateQuestionForm(question);

    });

    return this.builder.group(group);
  }

  // utility: generate array map for prefill
  generateArrayMap(content: any) {
    if (!content) {
      return {};
    }

    const updatedContent = window['JSON'].parse(window['JSON'].stringify(content));

    // get all arrays
    let arrayMap = pickBy(updatedContent, (value, key) => {
      return isArray(value);
    });

    // transform value
    arrayMap = mapValues(arrayMap, (value, key) => {
      return value.length > 0 ? value.length : 1;
    });

    return arrayMap;
  }

  // utility: generate nested array map for prefill
  generateNestedArraysMap(prefill: any) {
    if (!prefill) {
      return {};
    }

    // clone prefill
    const updatedPrefill = window['JSON'].parse(window['JSON'].stringify(prefill));

    // pick array items
    const arrayItems = pickBy(updatedPrefill, (value, key) => {
      return isArray(value) && value.length > 0 && every(value, isObject);
    });

    // check for existence of array items
    if (isEmpty(arrayItems)) {
      return {};
    }

    // look for nested arrays
    let nestedArrays = mapValues(arrayItems, (value, key, object) => {
      return this.processArrayItems(value);
    });

    nestedArrays = flatten(values(nestedArrays));

    let nested = {};
    forEach(nestedArrays, (value) => {
      forEach(value, (valueInner, keyInner) => {
        nested[keyInner] = nested[keyInner] ? [...nested[keyInner], valueInner] : [valueInner];
      })
    });

    // find the maximun length
    nested = mapValues(nested, (value: any, key, object) => {
      return Math.max(...value);
    });

    return nested;
  }

  // utility: generate nested array map for prefill
  processArrayItems(items) {
    return items.map((value) => {
      const areArrays = pickBy(value, isArray);

      return mapValues(areArrays, (val, key, object) => {
        return val.length;
      });
    });
  }

  // get full question object
  getQuestion(questions: any, questionName: string) {
    if (!questionName) {
      return null;
    }

    return find(questions, { QuestionName: questionName});
  }

  // get question.QuestionName descendants
  getChildQuestions(question: any, questions: any, omitNestedArrayQuestions: boolean = false) {
    const descendents = this.listDescendantQuestions(question, clone(questions), [], omitNestedArrayQuestions);
    return descendents.slice(1);
  }

  listDescendantQuestions(question, questions, array, omitNestedArrayQuestions) {

    if (question.ChildQuestions) {
      question.ChildQuestions.forEach(desc => {
        const q: any = find(questions, desc);
        if (!omitNestedArrayQuestions || !(q.MultipleResponseChild === true && q.MultipleResponse === true)) {
          this.listDescendantQuestions(q, questions, array, omitNestedArrayQuestions);
        }
      });
    }

    array.push(questions.find(x => x.QuestionName == question.QuestionName ));

    return orderBy(array, ['Order'], ['asc']);

  }

}
